import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import * as THREE from "three";
import { ArrowDownRight, MountainSnow } from "lucide-react";

/**
 * Hero with imperative three.js (no R3F JSX) to bypass dev-tool
 * line-number prop injection that breaks @react-three/fiber's applyProps.
 */
export default function Hero() {
  const sectionRef = useRef(null);
  const mountRef = useRef(null);
  const scrollRef = useRef(0);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  scrollYProgress.on("change", (v) => {
    scrollRef.current = v;
  });

  const titleY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const subOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#080808");
    scene.fog = new THREE.Fog("#080808", 8, 22);

    const camera = new THREE.PerspectiveCamera(46, width / height, 0.1, 100);

    // Position camera adaptively: closer on wide desktops so the mountain fills
    // more of the frame, farther on tall mobile so the whole peak is visible.
    const setCameraForAspect = () => {
      const aspect = width / height;
      // distanceFactor: 1.0 at portrait/mobile, ~0.7 on wide desktop
      const distanceFactor = aspect > 1.4 ? Math.max(0.65, 1 - (aspect - 1.4) * 0.18) : 1.0;
      const dx = 5.4 * distanceFactor;
      const dy = 2.4 * distanceFactor;
      const dz = 5.9 * distanceFactor;
      camera.position.set(dx, dy, dz);
      camera.lookAt(0, 0.5, 0);
    };
    setCameraForAspect();

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const key = new THREE.DirectionalLight(0xf3eadb, 1.2);
    key.position.set(5, 6, 4);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xb97a3d, 0.5);
    rim.position.set(-4, 2, -3);
    scene.add(rim);

    // Group
    const group = new THREE.Group();
    scene.add(group);

    // Mountain base plate
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(3.6, 3.9, 0.25, 8, 1),
      new THREE.MeshStandardMaterial({ color: "#2c2820", roughness: 0.98, metalness: 0.04 })
    );
    // Make base material transparent so it can fade
    base.material.transparent = true;
    base.position.y = -2.0;
    group.add(base);

    // Mountain strata: stacked tapering layers forming a peaked silhouette.
    // Each layer is a low-poly "rock disc" that progressively shrinks toward the top.
    const strataCount = 22;
    const strata = [];
    const totalHeight = 3.8;
    const baseRadius = 3.6;

    // Two-peak silhouette: layers between i=15..18 get a secondary offset bump
    for (let i = 0; i < strataCount; i++) {
      const t = i / (strataCount - 1); // 0 bottom -> 1 top
      const yOffset = -1.85 + t * totalHeight;
      const layerHeight = 0.26 + (1 - t) * 0.06;

      // Pyramidal taper with slight curve (alpine profile), with minimum radius to stay visible
      let radius = baseRadius * Math.pow(1 - t, 0.7);
      radius = Math.max(radius, 0.32);
      // Irregular jitter so it feels like rough rock
      radius *= 0.92 + Math.random() * 0.16;

      // Random radial sides 5..7 for organic feel
      const sides = 5 + (i % 3);
      const rotation = (i * 0.37) + Math.random() * 0.4;

      // Slight horizontal offset to suggest a leaning ridge
      const ridgeOffsetX = Math.sin(t * Math.PI * 1.1) * 0.18 + (Math.random() - 0.5) * 0.05;
      const ridgeOffsetZ = Math.cos(t * Math.PI * 0.9) * 0.14 + (Math.random() - 0.5) * 0.05;

      // Color gradient from warm umber (base) to cooler stone (peak with snow tint at very top)
      let color;
      if (t > 0.88) color = new THREE.Color("#cfc7b8"); // snowy crest
      else if (t > 0.65) color = new THREE.Color("#6b6557").lerp(new THREE.Color("#a69c8a"), (t - 0.65) / 0.23);
      else color = new THREE.Color("#3f3a30").lerp(new THREE.Color("#6b6557"), t / 0.65);

      const mat = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.92,
        metalness: 0.08,
        flatShading: true,
        transparent: true,
        opacity: 1,
      });

      const geo = new THREE.CylinderGeometry(radius * 0.85, radius, layerHeight, sides, 1);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(ridgeOffsetX, yOffset, ridgeOffsetZ);
      mesh.rotation.y = rotation;
      group.add(mesh);

      // Wireframe edge overlay (thin etched contour lines)
      const wfMat = new THREE.MeshBasicMaterial({
        color: "#a69c8a",
        wireframe: true,
        transparent: true,
        opacity: 0.16,
      });
      const wfGeo = new THREE.CylinderGeometry(
        radius * 0.85 * 1.005,
        radius * 1.005,
        layerHeight * 1.01,
        sides,
        1
      );
      const wf = new THREE.Mesh(wfGeo, wfMat);
      wf.position.copy(mesh.position);
      wf.rotation.copy(mesh.rotation);
      group.add(wf);

      // Fly direction: primarily toward the viewer (positive camera direction)
      // with random angular spread, so pieces explode out of the screen smoothly.
      const camDir = new THREE.Vector3(7.2, 2.8, 7.8).normalize();
      // Random spread cone around the camera direction
      const spreadAngle = (Math.random() - 0.5) * 0.9; // ~±25°
      const spreadAxis = new THREE.Vector3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5
      ).normalize();
      const flyDir = camDir.clone().applyAxisAngle(spreadAxis, spreadAngle);
      // Add a sideways radial bias so pieces fan out across the viewport
      const sidewaysAngle = Math.random() * Math.PI * 2;
      const sideways = new THREE.Vector3(
        Math.cos(sidewaysAngle),
        0,
        Math.sin(sidewaysAngle)
      ).multiplyScalar(0.45 + Math.random() * 0.7);
      flyDir.add(sideways).normalize();

      const speed = 9 + Math.random() * 6; // big enough to leave the frame by p=1
      const drift = flyDir.multiplyScalar(speed);

      const spin = new THREE.Vector3(
        (Math.random() - 0.5) * 5.0,
        (Math.random() - 0.5) * 6.0,
        (Math.random() - 0.5) * 5.0
      );

      strata.push({
        mesh,
        wf,
        baseY: yOffset,
        baseX: ridgeOffsetX,
        baseZ: ridgeOffsetZ,
        baseRotY: rotation,
        layerT: t,
        drift,
        spin,
      });
    }

    // Snow cap accent at the very peak
    const cap = new THREE.Mesh(
      new THREE.ConeGeometry(0.45, 0.7, 6),
      new THREE.MeshStandardMaterial({
        color: "#e8e1d2",
        roughness: 0.85,
        metalness: 0.05,
        flatShading: true,
        transparent: true,
        opacity: 1,
      })
    );
    cap.position.y = -1.85 + totalHeight + 0.15;
    group.add(cap);
    strata.push({
      mesh: cap,
      wf: null,
      baseY: cap.position.y,
      baseX: 0,
      baseZ: 0,
      baseRotY: 0,
      layerT: 1.05,
      drift: new THREE.Vector3(7.2, 2.8, 7.8).normalize().multiplyScalar(11),
      spin: new THREE.Vector3(
        (Math.random() - 0.5) * 5.0,
        (Math.random() - 0.5) * 6.0,
        (Math.random() - 0.5) * 5.0
      ),
    });

    // Dust particles
    const particleCount = 800;
    const positions = new Float32Array(particleCount * 3);
    const speeds = new Float32Array(particleCount);
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 8;
      positions[i * 3 + 1] = Math.random() * 4 - 2;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 8;
      speeds[i] = 0.2 + Math.random() * 0.8;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const dustMat = new THREE.PointsMaterial({
      size: 0.035,
      color: "#c0b6a5",
      transparent: true,
      opacity: 0.15,
      depthWrite: false,
      sizeAttenuation: true,
    });
    const dust = new THREE.Points(dustGeo, dustMat);
    scene.add(dust);

    // Glow ring
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(2.6, 4.2, 64),
      new THREE.MeshBasicMaterial({ color: "#a69c8a", transparent: true, opacity: 0.06, side: THREE.DoubleSide })
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = -2.05;
    scene.add(ring);

    const clock = new THREE.Clock();
    let frameId;

    const onResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      setCameraForAspect();
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    const animate = () => {
      const t = clock.getElapsedTime();
      const delta = clock.getDelta();

      // Compute scroll progress imperatively (Lenis virtual scroll bypasses some hooks)
      let p = 0;
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const total = Math.max(1, rect.height - window.innerHeight);
        p = Math.max(0, Math.min(1, -rect.top / total));
      }
      scrollRef.current = p;

      group.rotation.y = t * 0.05 + p * 0.18;
      group.rotation.x = -0.02 + Math.sin(t * 0.4) * 0.02;
      group.position.y = -p * 0.15;

      // Float effect
      group.position.y += Math.sin(t * 1.2) * 0.04;

      // Smooth ease-in-out so motion feels continuous, not jerky
      const easeInOut = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);

      strata.forEach((s) => {
        // Each piece has its own carving window. Top first, bottom last,
        // but windows overlap heavily for a continuous flowing breakdown.
        const start = (1 - s.layerT) * 0.55;
        const span = 0.55; // how long the piece takes to fully exit
        const raw = (p - start) / span;
        const carve = Math.min(1, Math.max(0, raw));
        const eased = easeInOut(carve);

        // Squared progress accelerates motion (quadratic = "inertia + acceleration")
        const k = eased * eased;

        // Position: fly outward toward viewer + drift accel
        s.mesh.position.x = s.baseX + s.drift.x * k;
        s.mesh.position.y = s.baseY + s.drift.y * k;
        s.mesh.position.z = s.baseZ + s.drift.z * k;

        // Continuous rotation while flying
        s.mesh.rotation.x = s.spin.x * eased;
        s.mesh.rotation.y = s.baseRotY + s.spin.y * eased;
        s.mesh.rotation.z = s.spin.z * eased;

        // Stay full-size while flying, with a subtle scale-up so pieces feel
        // more present as they approach the camera (cinematic perspective).
        const scale = 1 + eased * 0.6;
        s.mesh.scale.setScalar(scale);
        s.mesh.material.opacity = Math.max(0, 1 - Math.pow(eased, 1.6));

        if (s.wf) {
          s.wf.position.copy(s.mesh.position);
          s.wf.rotation.copy(s.mesh.rotation);
          s.wf.scale.copy(s.mesh.scale);
          s.wf.material.opacity = 0.16 * (1 - eased);
        }
      });

      const arr = dust.geometry.attributes.position.array;
      for (let i = 0; i < particleCount; i++) {
        arr[i * 3 + 1] += speeds[i] * delta * (0.4 + p * 1.8);
        if (arr[i * 3 + 1] > 4) {
          arr[i * 3 + 1] = -2;
          arr[i * 3] = (Math.random() - 0.5) * 8;
          arr[i * 3 + 2] = (Math.random() - 0.5) * 8;
        }
      }
      dust.geometry.attributes.position.needsUpdate = true;
      // Dust intensifies mid-scroll, fades at the very end (everything gone)
      const dustEnvelope = Math.sin(Math.min(1, p * 1.2) * Math.PI);
      dust.material.opacity = 0.05 + dustEnvelope * 0.45;

      // Base + ring fade out toward the end of scroll
      const groundFade = Math.max(0, 1 - Math.max(0, p - 0.55) * 2.5);
      base.material.opacity = groundFade;
      ring.material.opacity = 0.06 * groundFade;

      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative w-full min-h-[140vh] overflow-hidden"
    >
      <div className="sticky top-0 h-screen w-full">
        <div ref={mountRef} data-testid="hero-canvas" className="absolute inset-0" />

        <div className="absolute inset-0 hero-veil pointer-events-none" />

        <div className="absolute inset-0 flex flex-col justify-end section-pad pb-16 md:pb-20 pointer-events-none">
          <div className="absolute top-28 md:top-32 left-6 md:left-12 lg:left-24 flex items-center gap-3">
            <span className="block h-px w-10 bg-[var(--accent)]" />
            <span className="text-[0.7rem] tracking-[0.3em] uppercase text-neutral-400">
              GMB Graniti s.r.l. · Balma · 1840 — 2024
            </span>
          </div>

          <div className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col items-center gap-3 text-neutral-500">
            <span className="vertical-rl text-[0.65rem] tracking-[0.4em] uppercase">
              Scorri per scavare
            </span>
            <span className="block w-px h-16 bg-neutral-700" />
          </div>

          <motion.h1
            style={{ y: titleY }}
            className="font-display font-medium text-white leading-[0.92] tracking-tighter text-[clamp(3rem,9vw,8.5rem)]"
            data-testid="hero-title"
          >
            Scavato
            <br />
            <span className="text-[var(--accent)] italic font-editorial font-normal">
              nel tempo.
            </span>
          </motion.h1>

          <motion.div
            style={{ opacity: subOpacity }}
            className="mt-10 grid md:grid-cols-12 gap-6 items-end pointer-events-auto"
          >
            <p className="md:col-span-5 text-neutral-300 text-base md:text-lg leading-relaxed max-w-md">
              Dalle Antiche Cave della Balma alla sienite di oggi. Una storia
              di pietra, di picapere e di mestiere che continua a vivere.
            </p>
            <div className="md:col-span-3 md:col-start-7 hidden md:block">
              <p className="text-[0.7rem] tracking-[0.25em] uppercase text-neutral-500 mb-2">
                Materiale
              </p>
              <p className="font-display text-2xl text-neutral-100">Sienite della Balma</p>
            </div>
            <div className="md:col-span-3 flex md:justify-end gap-3">
              <a href="#storia" data-testid="hero-cta-storia" className="btn-primary">
                Esplora la storia
                <ArrowDownRight size={16} />
              </a>
              <a href="#sienite" data-testid="hero-cta-sienite" className="btn-ghost">
                <MountainSnow size={14} />
                Sienite
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
